import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Typography, Box } from "@mui/material";
import { FileSpreadsheet } from "lucide-react";

import Card from "../../Card";
import { UIButton, UIUploadProgress } from "../../ui";
import { clx } from "../../../utils/Helpers";

import { useSelector } from "react-redux";
import messages from "../../../assets/locale/messages";

import "./_CompanyEditDocuments.scss";

const MAX_PDF_SIZE = 5 * 1024 * 1024;

const CompanyEditDocuments = ({
  commercialRecordUrl,
  delegationDocumentUrl,
  idDocumentUrl,
  pendingDocs,
  onDocumentChange,
  classNames,
  uploadState,
  uploadProgressLabel,
  disabled,
  showReplace,
  showMissingDocuments,
}) => {
  const lang = useSelector((state) => state.locale.lang);
  const { companiesList: t } = messages[lang];

  const fileInputRefs = useRef({});
  const [docErrors, setDocErrors] = useState({});

  const documentsConfig = [
    {
      key: "delegation_document_url",
      label: t.delegationDocument,
      url: delegationDocumentUrl,
      pendingFile:
        pendingDocs?.delegation_document_url instanceof File
          ? pendingDocs.delegation_document_url
          : undefined,
      accept: ".pdf",
    },
    {
      key: "personal_id_url",
      label: t.idDocument,
      url: idDocumentUrl,
      pendingFile:
        pendingDocs?.personal_id_url instanceof File
          ? pendingDocs.personal_id_url
          : undefined,
      accept: ".pdf,.jpg,.jpeg,.png",
    },
    {
      key: "commercial_record_url",
      label: t.commercialRecordDocument,
      url: commercialRecordUrl,
      pendingFile:
        pendingDocs?.commercial_record_url instanceof File
          ? pendingDocs.commercial_record_url
          : undefined,
      accept: ".pdf",
    },
  ];

  const documentRows = showMissingDocuments
    ? documentsConfig
    : documentsConfig.filter((doc) => doc.url || doc.pendingFile);
  const handleDownload = (doc) => {
    if (!doc.url) return;
    window.open(doc.url, "_blank", "noopener,noreferrer");
  };

  // Allowed MIME types for mixed PDF+image documents (personal ID).
  // Using an explicit allowlist instead of `file.type.startsWith("image/")`
  // ensures exotic formats (GIF, WebP, TIFF, etc.) are rejected on the
  // frontend before an S3 upload reaches the backend.
  const ALLOWED_IMAGE_TYPES = ["image/jpeg", "image/jpg", "image/png"];

  const handleReplace = (docKey, accept, e) => {
    if (disabled) return;
    const file = e.target.files?.[0];
    if (!file) return;
    const pdfOnly = accept === ".pdf";
    const isValidType = pdfOnly
      ? file.type === "application/pdf"
      : file.type === "application/pdf" ||
        ALLOWED_IMAGE_TYPES.includes(file.type);
    if (!isValidType) {
      setDocErrors((prev) => ({
        ...prev,
        [docKey]: pdfOnly
          ? t.pdfUploadTypeError ?? "Accepted format is PDF only"
          : t.uploadTypeError ?? "Accepted formats: PDF, JPG, PNG",
      }));
      e.target.value = "";
      return;
    }
    if (file.size > MAX_PDF_SIZE) {
      setDocErrors((prev) => ({
        ...prev,
        [docKey]: t.pdfUploadSizeError ?? "Max size is 5MB",
      }));
      e.target.value = "";
      return;
    }
    setDocErrors((prev) => ({ ...prev, [docKey]: null }));
    onDocumentChange?.(docKey, file);
    e.target.value = "";
  };

  return (
    <Card
      title={t.documents}
      classNames={{
        root: clx(
          "company-edit-documents",
          !showReplace && "company-edit-documents--view",
          classNames?.root,
        ),
        title: classNames?.title,
        body: clx("company-edit-documents__body", classNames?.body),
      }}
    >
      <Box className="company-edit-documents__list">
        {documentRows.map((doc) => {
          const displayName = doc.pendingFile
            ? doc.pendingFile.name
            : doc.url?.split("/").pop() || "";
          const isRowUploading = uploadState?.target === doc.key;
          return (
            <Box key={doc.key}>
              <Box
                className={clx("company-edit-documents__row", classNames?.row)}
              >
                <Box className="d-flex align-items-center gap-3">
                  <Box
                    className={clx(
                      "company-edit-documents__icon",
                      classNames?.icon,
                    )}
                  >
                    <FileSpreadsheet size={24} strokeWidth={2} />
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0, overflow: "hidden" }}>
                    <Typography
                      className={clx(
                        "company-edit-documents__label",
                        classNames?.label,
                      )}
                    >
                      {doc.label}
                    </Typography>
                    {displayName && (
                      <Typography
                        title={displayName}
                        className={clx(
                          "company-edit-documents__name",
                          classNames?.name,
                        )}
                      >
                        {displayName}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box className="company-edit-documents__actions">
                  {doc.url && (
                    <UIButton
                      type="button"
                      variant="outline"
                      size="medium"
                      disabled={disabled}
                      onClick={() => handleDownload(doc)}
                      className={clx(
                        "company-edit-documents__download-button",
                        classNames?.downloadButton,
                      )}
                    >
                      {t.download}
                    </UIButton>
                  )}
                  {showReplace && (
                    <>
                      <UIButton
                        type="button"
                        variant="outline"
                        size="medium"
                        disabled={disabled}
                        onClick={() => fileInputRefs.current[doc.key]?.click()}
                        className={clx(
                          "company-edit-documents__replace-button",
                          classNames?.replaceButton,
                        )}
                      >
                        {t.replace}
                      </UIButton>
                      <input
                        type="file"
                        accept={doc.accept ?? ".pdf,.jpg,.jpeg,.png"}
                        style={{ display: "none" }}
                        disabled={disabled}
                        ref={(el) => {
                          fileInputRefs.current[doc.key] = el;
                        }}
                        onChange={(e) =>
                          handleReplace(
                            doc.key,
                            doc.accept ?? ".pdf,.jpg,.jpeg,.png",
                            e,
                          )
                        }
                      />
                    </>
                  )}
                </Box>
              </Box>
              {docErrors[doc.key] && (
                <Typography
                  variant="caption"
                  sx={{ color: "error.main", mt: 0.5, display: "block" }}
                >
                  {docErrors[doc.key]}
                </Typography>
              )}
              {isRowUploading && (
                <UIUploadProgress
                  fileName={displayName || doc.label}
                  isPdf
                  progress={uploadState.percent}
                  label={uploadProgressLabel}
                />
              )}
            </Box>
          );
        })}
      </Box>
    </Card>
  );
};

CompanyEditDocuments.propTypes = {
  onDocumentChange: PropTypes.func.isRequired,
  classNames: PropTypes.object,
  commercialRecordUrl: PropTypes.string,
  delegationDocumentUrl: PropTypes.string,
  idDocumentUrl: PropTypes.string,
  pendingDocs: PropTypes.object,
  uploadState: PropTypes.shape({
    target: PropTypes.string,
    percent: PropTypes.number,
  }),
  uploadProgressLabel: PropTypes.string,
  disabled: PropTypes.bool,
  showReplace: PropTypes.bool,
  showMissingDocuments: PropTypes.bool,
};

CompanyEditDocuments.defaultProps = {
  uploadState: null,
  uploadProgressLabel: undefined,
  disabled: false,
  showReplace: true,
  showMissingDocuments: true,
  classNames: {
    root: "",
    body: "",
    row: "",
    icon: "",
    label: "",
    name: "",
  },
};

export default CompanyEditDocuments;
